import { useEffect, useState } from "react"
import {
  LabelMedium,
  ModalBody,
  ModalFooter,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import FailedActionModalBody from "#/shared/components/modals/FailedActionModalBody"
import ProgressSteps, { NumberedStep } from "#/shared/components/ProgressSteps"
import { MezoNativeBTC } from "#/shared/constants/artifacts"
import { address as matsnetVeBTCAddress } from "#/shared/constants/artifacts/VeBTC"
import {
  useApproveERC20Token,
  useBTCToUSDConversion,
  useEstimateVeBTCValue,
  useStakeBtc,
} from "#/shared/hooks"
import {
  floatToBigInt,
  networkUtils,
  separateThousandsWithComma,
  stakingUtils,
} from "#/shared/utils"
import {
  ComponentStepProps,
  SignTransactionModalButton,
  useStepperModalContext,
} from "#/ui/Modal"

import {
  ReviewDetails,
  ReviewDetailsListItem,
  StakingFormValue,
  StakingRewardsLabel,
} from "../components"

import WaitingForStakingTransaction from "./WaitingForStakingTransaction"

export default function ReviewAndSignStep({
  onClickContinue,
}: ComponentStepProps) {
  const [css, theme] = useStyletron()

  const {
    values: { amount, lockDuration },
  } = useStepperModalContext<StakingFormValue>()

  const amountToStake = floatToBigInt(amount)
  const amountUSD = useBTCToUSDConversion(amount)
  const veBTC = useEstimateVeBTCValue(floatToBigInt(amount), lockDuration!)

  const [step, setStep] = useState(0)

  const {
    stake,
    isPending: isStakingTxPending,
    isError,
    isWaitingForTxReceipt: isWaitingForStakingTxReceipt,
    isTxConfirmed: isStakingTxConfirmed,
  } = useStakeBtc()

  useEffect(() => {
    if (!isStakingTxConfirmed) return

    onClickContinue()
  }, [isStakingTxConfirmed, onClickContinue])

  const {
    approvedAmount,
    approve,
    isFetchingApprovedAmount,
    isPending: isApprovalTxPending,
    isTxConfirmed: isApproveTxConfirmed,
    isWaitingForTxReceipt: isWaitingForApproveTxReceipt,
  } = useApproveERC20Token(MezoNativeBTC.address, matsnetVeBTCAddress)

  useEffect(() => {
    if (approvedAmount && approvedAmount >= amountToStake && step === 0) {
      setStep(1)
    }
  }, [approvedAmount, amountToStake, step, isApproveTxConfirmed])

  const onSubmitStep = async () => {
    if (approvedAmount! < amountToStake) {
      await approve(amountToStake)
      return
    }

    await stake({ lockDuration, value: amountToStake })
  }

  const txDetails: ReviewDetailsListItem[] = [
    {
      label: "Amount",
      endEnhancer: (
        <>
          <LabelMedium>{amount} BTC</LabelMedium>
          <ParagraphSmall color={theme.colors.contentSecondary}>
            $ {separateThousandsWithComma(amountUSD ?? 0, 0)}
          </ParagraphSmall>
        </>
      ),
    },
    {
      label: "Lock duration",
      endEnhancer: (
        <LabelMedium style={{ textTransform: "lowercase" }}>
          {stakingUtils.getLockDurationLabel(lockDuration)}
        </LabelMedium>
      ),
    },
  ]

  const rewardsDetails: ReviewDetailsListItem[] = [
    {
      label: "Amount",
      endEnhancer: <StakingRewardsLabel veBTC={veBTC} />,
    },
  ]

  if (isStakingTxPending || isWaitingForStakingTxReceipt)
    return <WaitingForStakingTransaction />

  if (isError)
    return (
      <FailedActionModalBody
        title="Something went wrong"
        description="Please try again."
        nextStepButtonLabel="Try again"
        onClickNextStep={onSubmitStep}
      />
    )

  return (
    <>
      <ModalBody
        className={css({
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: theme.sizing.scale600,
        })}
      >
        <ReviewDetails label="Stake details" items={txDetails} />
        <ReviewDetails
          label="Rewards"
          items={rewardsDetails}
          backgroundColor={theme.colors.lime300}
        />
        <ProgressSteps currentStep={step}>
          <NumberedStep
            title={
              approvedAmount! >= amountToStake ? "BTC approved" : "Approve BTC"
            }
            isPending={
              isApprovalTxPending ||
              isFetchingApprovedAmount ||
              isWaitingForApproveTxReceipt
            }
            pendingText={
              isWaitingForApproveTxReceipt || isApprovalTxPending
                ? "Waiting for transaction..."
                : "Checking approval..."
            }
          />
          <NumberedStep title="Stake BTC" />
        </ProgressSteps>
      </ModalBody>
      <ModalFooter
        className={css({
          display: "block",
        })}
      >
        <SignTransactionModalButton
          onClick={onSubmitStep}
          gasFee="$1.2"
          buttonLabel={`Sign on ${networkUtils.getMezoDAppLabel()}`}
          isLoading={
            isFetchingApprovedAmount ||
            isApprovalTxPending ||
            isWaitingForApproveTxReceipt
          }
        />
      </ModalFooter>
    </>
  )
}
